import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { filter, map, Observable, Subject, takeUntil } from 'rxjs';

import {
  DcSensorValueProperties,
  DeviceFirmwareVersion,
  DeviceNameCommands,
  DeviceNameProperties,
  Feature,
  FeatureNames,
  FeaturePropertyKey,
  IsStringValueProperty,
  SignalProperties,
  TemperatureProperty,
} from '@vi-iot/client';

import { lowLqiSignalThreshold } from '@common';
import {
  DevicesStore,
  GatewayDevice,
  GatewayStore,
  InstallationFeaturesStore,
  RemoteDeviceDetails,
} from '@store';

import { FeatureTileIconVariant } from '../../../../shared/components/feature-tile/feature-tile-variants';
import { NO_VALUE } from 'src/app/shared/pipes/dash/dash.pipe';
import { HeatingCircuitHelper } from '../../heating-circuit-features-panel/heating-circuit.helper';

@Component({
  selector: 'app-remote-controller-overlay',
  templateUrl: './remote-controller-overlay.component.html',
  styleUrls: ['./remote-controller-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RemoteControllerOverlayComponent implements OnInit, OnDestroy {
  readonly featurePropertyKey = FeaturePropertyKey;
  readonly lowLqiSignalThreshold = lowLqiSignalThreshold;
  readonly iconVariant = FeatureTileIconVariant;
  readonly primaryCircuitId = '0';
  readonly noValue = NO_VALUE;
  readonly heatingOperatingProgramsSchema = HeatingCircuitHelper.HeatingOperatingProgramsSchema;

  activeProgram: string;

  @Input() installationId: number;
  @Input() showTitleAndAction: boolean;
  @Input() device: RemoteDeviceDetails;
  @Output() confirmDelete: EventEmitter<GatewayDevice> = new EventEmitter<GatewayDevice>();
  firmware$: Observable<Feature<DeviceFirmwareVersion, never>>;
  temperature$: Observable<Feature<DcSensorValueProperties, never>>;
  humidity$: Observable<Feature<DcSensorValueProperties, never>>;
  targetTemperatue$: Observable<Feature<TemperatureProperty, never>>;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public devicesStore: DevicesStore,
    public gatewayDevices: GatewayStore,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  ngOnInit(): void {
    this.firmware$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.essFirmwareBundle,
      this.device.device.id
    );

    this.temperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceTemperatureSensor,
      this.device.device.id
    );

    this.humidity$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceHumiditySensor,
      this.device.device.id
    );

    this.installationFeaturesStore
      .selectInstallationDeviceFeature<IsStringValueProperty, never>(
        FeatureNames.circuitActiveProgram(this.primaryCircuitId)
      )
      .pipe(
        map((feature) => feature?.properties?.value?.value),
        filter((activeProgram) => !!activeProgram)
      )
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((activeProgram: string) => {
        this.activeProgram = activeProgram;
        this.targetTemperatue$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
          TemperatureProperty,
          never
        >(FeatureNames.circuitOperatingProgram(this.primaryCircuitId, activeProgram));
      });
  }

  getLqiFeature(deviceId: string): Observable<Feature<SignalProperties, never>> {
    return this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceZigbeeLqi,
      deviceId
    );
  }

  getDeviceDescription(
    deviceId: string
  ): Observable<Feature<DeviceNameProperties, DeviceNameCommands>> {
    return this.installationFeaturesStore.selectInstallationDeviceFeature<
      DeviceNameProperties,
      DeviceNameCommands
    >(FeatureNames.deviceName, deviceId);
  }

  deleteRemoteDevice(device?: RemoteDeviceDetails): void {
    if (device && device.device) {
      const emitDevice = device.device;
      this.confirmDelete.emit(emitDevice);
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
